import React, { useState } from "react";
import { Sparkles, Download, Loader2 } from "lucide-react";
import api from "@/lib/api";
import Markdown from "@/components/Markdown";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Kundali() {
  const [form, setForm] = useState({
    full_name: "", dob: "", tob: "", pob: "", gender: "male"
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!user || user === false) { nav("/login", { state: { from: "/kundali" } }); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/kundali/generate", form);
      setResult(data);
      toast.success("Kundali cast ✨");
    } catch (err) {
      toast.error("Could not generate Kundali. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Auto AI Kundali</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight max-w-3xl">
          Cast your birth chart in <span className="italic gold-gradient">seconds.</span>
        </h1>
        <p className="text-[#A0A4AB] mt-4 max-w-xl">
          Powered by Gemini AI trained on Vedic astrology. Enter your birth details — receive Lagna,
          Rashi, Nakshatra, Dasha & personalised remedies.
        </p>
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <form onSubmit={submit} className="md:col-span-5 p-8 rounded-xl bg-[#0A0C10] border border-white/10 space-y-4 h-fit">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Full Name</label>
            <input
              data-testid="kundali-name-input"
              required value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
              placeholder="e.g. Arjun Verma"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Date of Birth</label>
              <input
                data-testid="kundali-dob-input"
                type="date" required value={form.dob}
                onChange={(e) => setForm({ ...form, dob: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Time of Birth</label>
              <input
                data-testid="kundali-tob-input"
                type="time" required value={form.tob}
                onChange={(e) => setForm({ ...form, tob: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Place of Birth</label>
            <input
              data-testid="kundali-pob-input"
              required value={form.pob}
              onChange={(e) => setForm({ ...form, pob: e.target.value })}
              className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"
              placeholder="City, Country"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Gender</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {["male","female","other"].map((g) => (
                <button
                  type="button" key={g}
                  data-testid={`kundali-gender-${g}`}
                  onClick={() => setForm({ ...form, gender: g })}
                  className={`py-2 text-sm rounded-md border capitalize transition ${
                    form.gender === g ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                                       : "border-white/10 text-[#A0A4AB] hover:border-white/20"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            data-testid="kundali-submit-btn"
            disabled={busy}
            className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Casting…</> : <><Sparkles size={16} /> Generate My Kundali</>}
          </button>
        </form>

        <div className="md:col-span-7">
          {!result && !busy && (
            <div className="p-10 rounded-xl border border-dashed border-white/10 bg-[#0A0C10]/50 text-center">
              <div className="font-deva text-5xl text-[#D4AF37] mb-4">ॐ</div>
              <div className="text-[#A0A4AB]">Your birth chart interpretation will appear here.</div>
            </div>
          )}
          {busy && (
            <div className="p-10 rounded-xl border border-white/10 bg-[#0A0C10] text-center">
              <Loader2 className="animate-spin mx-auto text-[#D4AF37]" size={32} />
              <div className="text-[#A0A4AB] mt-4">Casting your Kundali… reading planetary positions ✨</div>
            </div>
          )}
          {result && (
            <div data-testid="kundali-result" className="p-8 rounded-xl bg-[#0A0C10] border border-white/10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Personalised Kundali</div>
                  <div className="font-serif text-2xl">{result.input.full_name}</div>
                  <div className="text-xs text-[#A0A4AB] mt-1">
                    {result.input.dob} · {result.input.tob} · {result.input.pob}
                  </div>
                </div>
                <button className="text-sm inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E5C865]" onClick={() => window.print()}>
                  <Download size={14} /> Save
                </button>
              </div>
              <Markdown text={result.interpretation} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
